import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { pitchClass } from '@/lib/pitch';
import { midi } from '@/lib/midi';
import { routeToMediaElement } from '@/lib/backgroundAudio';
import { activeSounds } from '@/lib/activeSounds';
import { transportBus } from '@/lib/transportBus';
import { DEFAULT_PROG, QUALITIES, chordName, droneFreqs, droneKey, stepAt } from '@/lib/droneChords';
import { createVoice } from '@/lib/droneTimbres';

const XFADE = 0.06;
const KEY = 'tuner.drone';
const DEFAULTS = { pc: 0, octave: 3, fifth: false, volume: 0.5, timbre: 'organ', quality: 'maj', prog: DEFAULT_PROG, offsetMs: 0, transpose: 'C' };
const load = () => { try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || '{}') }; } catch { return { ...DEFAULTS }; } };
const initialParams = () => ({ a4: Number(localStorage.getItem('tuner.a4') || 440) });

const DroneContext = createContext(null);

function useDroneEngine() {
  const [cfg, setCfg] = useState(load);
  const [active, setActive] = useState(false);
  const [params, setParams] = useState(initialParams);
  const [barCount, setBarCount] = useState(0);
  const { a4 } = params;
  const { semitones, flats } = droneKey(cfg.transpose);
  const activeRef = useRef(active);
  activeRef.current = active;
  const ref = useRef({ group: null });

  const voicing = cfg.voicing || (cfg.fifth ? 'fifth' : 'root');
  const timbre = cfg.timbre || 'organ';
  const prog = cfg.prog?.steps?.length ? cfg.prog : { ...DEFAULT_PROG, enabled: false };
  const progOn = !!prog.enabled && prog.steps.length > 0;
  const stepIdx = progOn ? stepAt(prog.steps, barCount) : -1;
  const cur = progOn ? prog.steps[stepIdx] : { pc: cfg.pc, quality: cfg.quality || 'maj' };
  const rootPc = cur.pc;
  const quality = QUALITIES[cur.quality] ? cur.quality : 'maj';
  const concertMidi = (cfg.octave + 1) * 12 + rootPc - semitones;
  const just = cfg.temperament === 'just';
  const label = voicing === 'chord'
    ? chordName(rootPc, quality, flats)
    : `${pitchClass(rootPc, flats)}${cfg.octave}${voicing === 'fifth' ? '+5' : ''}`;

  const ensureCtx = () => {
    const r = ref.current;
    if (!r.ctx) {
      r.ctx = new (window.AudioContext || window.webkitAudioContext)();
      r.master = r.ctx.createGain();
      r.master.gain.value = 0;
      // Slow "breathing" swell (volume only, pitch untouched)
      r.swell = r.ctx.createGain();
      const lfo = r.ctx.createOscillator();
      const lfoDepth = r.ctx.createGain();
      lfo.frequency.value = 0.18;
      lfoDepth.gain.value = 0.05;
      lfo.connect(lfoDepth).connect(r.swell.gain);
      lfo.start();
      const lp = r.ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 4500;
      lp.Q.value = 0.5;
      r.master.connect(r.swell).connect(lp);
      r.el = routeToMediaElement(r.ctx, lp);
      r.ctx.onstatechange = () => { if (activeRef.current && r.ctx.state !== 'running' && r.ctx.state !== 'closed') r.ctx.resume().catch(() => {}); };
    }
    r.ctx.resume().catch(() => {});
    r.el?.play().catch(() => {});
    return r;
  };

  const fadeOutGroup = (group, r, at = r.ctx.currentTime) => {
    if (!group) return;
    const t = Math.max(at, r.ctx.currentTime);
    group.gain.gain.cancelScheduledValues(t);
    group.gain.gain.setTargetAtTime(0, t, XFADE / 3);
    group.voice.stop(t + XFADE * 4);
  };

  // Crossfades to a new set of voices so note/chord changes don't click.
  // `at` (drone ctx time) schedules a sample-accurate change on a future downbeat.
  const buildVoices = (freqs, at) => {
    const r = ensureCtx();
    const key = `${timbre}|${freqs.map((f) => f.toFixed(3)).join(',')}`;
    if (r.group && r.groupKey === key) return;
    const now = r.ctx.currentTime;
    const t = at === undefined ? now : Math.max(now, at);
    const gain = r.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.connect(r.master);
    const voice = createVoice(r.ctx, gain, freqs, timbre, t);
    gain.gain.setTargetAtTime(1, t, (at === undefined ? Math.max(XFADE, voice.attack) : XFADE) / 3);
    fadeOutGroup(r.group, r, t);
    r.group = { gain, voice };
    r.groupKey = key;
  };

  // Queues the chord of an upcoming downbeat exactly on the click (plus user offset).
  const scheduleBar = (count, { lead, latency }) => {
    const r = ref.current;
    if (!activeRef.current || !progOn || !r.ctx) return;
    const st = prog.steps[stepAt(prog.steps, count)];
    const m = (cfg.octave + 1) * 12 + st.pc - semitones;
    const comp = latency - (r.ctx.outputLatency || r.ctx.baseLatency || 0);
    buildVoices(droneFreqs(m, voicing, st.quality, just, a4), r.ctx.currentTime + lead + comp + (cfg.offsetMs || 0) / 1000);
  };
  const scheduleRef = useRef(scheduleBar);
  scheduleRef.current = scheduleBar;

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(cfg)); }, [cfg]);

  // Follows the most recently started metronome/sequencer/click: each of its downbeats advances the chords.
  useEffect(() => {
    const counter = { n: 0, leader: null };
    const reset = () => { counter.n = 0; setBarCount(0); };
    const offStart = transportBus.onStart((id) => { counter.leader = id; reset(); });
    const offBar = transportBus.onBar((info) => {
      if (counter.leader === null) counter.leader = info.id;
      if (info.id !== counter.leader) return;
      counter.n += 1;
      const n = counter.n;
      scheduleRef.current(n, info);
      setTimeout(() => { if (counter.leader === info.id && counter.n >= n) setBarCount(n); }, Math.max(0, info.lead * 1000));
    });
    const offStop = transportBus.onStop((id) => { if (id === counter.leader) { counter.leader = null; reset(); } });
    return () => { offStart(); offBar(); offStop(); };
  }, []);

  useEffect(() => {
    if (!active) return;
    buildVoices(droneFreqs(concertMidi, voicing, quality, just, a4));
  }, [active, concertMidi, voicing, quality, just, a4, timbre]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const r = ref.current;
    if (!r.master) return undefined;
    r.master.gain.setTargetAtTime(active ? cfg.volume * 0.6 : 0, r.ctx.currentTime, 0.08);
    if (active) return undefined;
    const id = setTimeout(() => { fadeOutGroup(r.group, r); r.group = null; r.groupKey = null; }, 500);
    return () => clearTimeout(id);
  }, [active, cfg.volume]);

  // Keeps playing with the screen locked; lock-screen Pause/Stop (or the bottom bar) turns it off.
  useEffect(() => {
    if (!active) { activeSounds.remove('drone'); return; }
    activeSounds.set('drone', { label: `Bordone ${label}`, detail: progOn ? 'a tempo' : '', stop: () => setActive(false) });
  }, [active, label, progOn]);

  const toggle = () => { if (!active) ensureCtx(); setActive((a) => !a); };
  // Keeps the legacy `fifth` flag (used by sharing) in sync with `voicing`.
  const update = (patch) => setCfg((c) => {
    const p = { ...patch };
    if ('voicing' in p) p.fifth = p.voicing === 'fifth';
    else if ('fifth' in p) p.voicing = p.fifth ? 'fifth' : 'root';
    return { ...c, ...p };
  });
  const updateProg = (patch) => update({ prog: { ...prog, ...patch } });

  // MIDI key (concert pitch) sets the written drone note — on the Tuner or while the drone is on.
  const semitonesRef = useRef(semitones);
  semitonesRef.current = semitones;
  useEffect(() => midi.onNote((note) => {
    if (!activeRef.current && window.location.pathname !== '/bordone') return;
    const written = note + semitonesRef.current;
    const octave = Math.max(1, Math.min(5, Math.floor(written / 12) - 1));
    setCfg((c) => ({ ...c, pc: ((written % 12) + 12) % 12, octave }));
    ensureCtx();
    setActive(true);
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    cfg, update, active, toggle, concertMidi, just, flats, setParams, a4, semitones,
    voicing, timbre, prog, progOn, updateProg, stepIdx, rootPc, quality, label, barCount,
  };
}

export const DroneProvider = ({ children }) => {
  const drone = useDroneEngine();
  return <DroneContext.Provider value={drone}>{children}</DroneContext.Provider>;
};

// Tuner passes its A4 so the drone follows it (the instrument key is the drone's own setting).
export function useDrone(params) {
  const drone = useContext(DroneContext);
  const a4 = params?.a4;
  useEffect(() => {
    if (a4) drone.setParams({ a4 });
  }, [a4]); // eslint-disable-line react-hooks/exhaustive-deps
  return drone;
}
